import { GameType } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { KAPLAYCtx } from "kaplay";
import { TrackingPageGame, VictoryState } from "./game";
import { GameCompleteCallback, GameControls } from "./game-controls";

export const galagaGame: TrackingPageGame = {
  gameType: GameType.GALAGA,
  initializeGame: initializeGalagaGame,
  strings: {},
  showControls: true,
};

export function initializeGalagaGame(
  k: KAPLAYCtx,
  onGameComplete: GameCompleteCallback,
): GameControls {
  let score = 0;
  let gameTime = 0;
  const gameLength = 45; // 45 seconds

  // Simplified Galaga game stub
  const player = k.add([
    k.rect(40, 20),
    k.pos(k.width() / 2, k.height() - 50),
    k.color(0, 255, 0),
    k.area(),
    { vel: k.vec2(0, 0) },
    "player",
  ]);

  // Score display
  const scoreText = k.add([
    k.text(`Score: ${score}`, { size: 16 }),
    k.pos(10, 10),
    k.color(255, 255, 255),
  ]);

  // Timer display
  const timerText = k.add([
    k.text(`Time: ${Math.ceil(gameLength - gameTime)}`, { size: 16 }),
    k.pos(10, 30),
    k.color(255, 255, 255),
  ]);

  // Add simple "enemies" that give points when player moves over them
  for (let i = 0; i < 5; i++) {
    k.add([
      k.rect(20, 20),
      k.pos(k.rand(20, k.width() - 20), k.rand(50, k.height() - 100)),
      k.color(255, 0, 0),
      k.area(),
      "enemy",
    ]);
  }

  // Update player position
  player.onUpdate(() => {
    // Update game time
    gameTime += k.dt();
    timerText.text = `Time: ${Math.ceil(gameLength - gameTime)}`;

    // Check if game should end
    if (gameTime >= gameLength) {
      onGameComplete({ finalScore: score, victoryState: VictoryState.VICTORY });
      return;
    }

    player.pos.x += player.vel.x * k.dt();

    // Keep player on screen
    player.pos.x = Math.max(20, Math.min(player.pos.x, k.width() - 20));
  });

  // Add collision detection with enemies
  player.onCollide("enemy", (enemy) => {
    k.destroy(enemy);
    score += 200;
    scoreText.text = `Score: ${score}`;

    // Spawn a new enemy
    k.add([
      k.rect(20, 20),
      k.pos(k.rand(20, k.width() - 20), k.rand(50, k.height() - 100)),
      k.color(255, 0, 0),
      k.area(),
      "enemy",
    ]);
  });

  // Add title
  k.add([
    k.text("Galaga Style Game", { size: 18 }),
    k.pos(k.width() / 2, 20),
    k.anchor("center"),
    k.color(255, 255, 255),
  ]);

  // Add instructions
  k.add([
    k.text("Use controls below", { size: 12 }),
    k.pos(k.width() / 2, k.height() - 20),
    k.anchor("center"),
    k.color(255, 255, 255),
  ]);

  // Movement controls (keyboard)
  k.onKeyDown("left", () => {
    player.vel.x = -300;
  });
  k.onKeyDown("right", () => {
    player.vel.x = 300;
  });

  // Stop movement on key release
  k.onKeyRelease(["left", "right"], () => {
    player.vel.x = 0;
  });

  // Return mobile control functions
  return {
    moveLeft: () => {
      player.vel.x = -300;
    },
    moveRight: () => {
      player.vel.x = 300;
    },
    moveUp: () => {}, // Not used in Galaga
    moveDown: () => {}, // Not used in Galaga
    stopMovement: () => {
      player.vel.x = 0;
    },
  };
}
